<!--
GraceBanner — presentational banner for the itemized subscription grace state.

Receives the structured 'subscription' keyed banner payload from useGlobalBanners:
  { key: 'subscription', items: [{ franchise_name, days_left, franchise_id, status }], severity }

Renders ONE banner that lists EVERY in-grace franchise by name with its days-left.
Never renders a generic string — always itemized, always named.

CTA navigates to /pagamento-e-uso.

ESCALONAMENTO VISUAL (S12/M6) — a cor sai do MESMO days_remaining que a linha já
carrega, vindo da autoridade no backend (Subscriptions_MyFranchiseRow, #24).
Nada de recalcular datas no cliente: o banner, o console e o corte precisam
contar os mesmos dias, e uma terceira régua no navegador é exatamente a
divergência staff-vs-franqueado que o M0 matou.

  6-10 dias → neutro  (info)   "ainda dá tempo"
  4-5  dias → atenção (warn)
  1-3  dias → crítico (error)  — inclui 0, o último dia inteiro

A banda vale para a franquia MAIS urgente da lista: com várias franquias em
carência, a que corta primeiro decide a cor de todas — pintar de neutro um
banner que contém uma franquia a 2 dias esconderia a urgência atrás da média.
-->

<template>
  <div
    class="grace-banner"
    :class="`grace-banner--${severity}`"
    :data-severity="severity"
    data-testid="grace-banner"
  >
    <i class="grace-banner__icon pi" :class="severityIcon" />
    <div class="grace-banner__body">
      <strong class="grace-banner__title">{{ severityTitle }}</strong>
      <span class="grace-banner__message">
        <span
          v-for="item in items"
          :key="item.franchise_id"
          class="grace-banner__item"
        >
          <strong>{{ item.franchise_name }}</strong>
          <template v-if="item.days_left != null">
            <!-- Plural pela convenção viva do repo (MyFranchisesPage, PDVEntregasNotifyModal):
                 t() só na palavra estática. A sobrecarga de pluralização do vue-i18n
                 t('{n} dia', n) resolvia para a forma ÚNICA em qualquer locale sem plural
                 registrado — com formatFallbackMessages a chave vira a própria mensagem —,
                 e renderizava "(5 dia)". -->
            {{ ' ' }}· {{ item.days_left }} {{ item.days_left === 1 ? t('dia') : t('dias') }}
          </template>
        </span>
      </span>
    </div>
    <AppButton
      v-if="showCta"
      :label="t('Ver assinaturas')"
      icon="pi pi-arrow-right"
      icon-pos="right"
      size="small"
      class="grace-banner__cta"
      data-testid="grace-banner-cta"
      @click="router.push(BILLING_RECOVERY_PATH)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { BILLING_RECOVERY_PATH } from '../composables/useSubscriptionStatus'
import type { GlobalBanner } from '../composables/useGlobalBanners'

interface GraceItem {
  franchise_name: string
  days_left: number | null
  franchise_id: number
  status: string
}

interface GraceBannerPayload extends GlobalBanner {
  items: GraceItem[]
  severity: 'warn' | 'error'
}

type GraceSeverity = 'info' | 'warn' | 'error'

const props = defineProps<{
  banner: GraceBannerPayload
}>()

const { t } = useI18n()
const router = useRouter()
// Optional chained: the integration mount installs no router. The CTA
// navigates to /minhas-assinaturas — pointless when already there.
const route = useRoute()
const showCta = computed<boolean>(() => route?.path !== BILLING_RECOVERY_PATH)

const items = computed(() => props.banner.items)

/**
 * A franquia mais urgente da lista — a que corta primeiro. `days_left` já vem
 * da autoridade (grace_days_remaining); linhas sem contagem não participam da
 * escolha da banda em vez de virarem um 0 inventado.
 */
const mostUrgentDaysLeft = computed<number | null>(() => {
  const counted = items.value
    .map((i) => i.days_left)
    .filter((d): d is number => typeof d === 'number')

  return counted.length > 0 ? Math.min(...counted) : null
})

const severity = computed<GraceSeverity>(() => {
  const days = mostUrgentDaysLeft.value

  // Sem contagem, nada a escalonar: fica o que o publicador decidiu.
  if (days === null) return props.banner.severity ?? 'warn'

  if (days <= 3) return 'error'
  if (days <= 5) return 'warn'
  return 'info'
})

const severityIcon = computed<string>(() => {
  if (severity.value === 'error') return 'pi-exclamation-triangle'
  if (severity.value === 'warn') return 'pi-clock'
  return 'pi-info-circle'
})

// Título por banda — o texto acompanha a urgência; os dias exatos ficam nos
// itens (a contagem vem da autoridade, nunca recalculada aqui).
const severityTitle = computed<string>(() => {
  if (severity.value === 'error') return t('Pagamento pendente — o acesso será suspenso em breve')
  if (severity.value === 'warn') return t('Pagamento pendente — período de carência')
  return t('Assinatura com pagamento pendente')
})
</script>

<style scoped>
/* Faixa cheia e chamativa (decisão do usuário 2026-08-12: os banners eram
   opacos demais). O host (GlobalBanners) zera o próprio padding via
   :has(.grace-banner), então esta superfície é dona do visual inteiro.
   A banda continua mudando SÓ a cor entre severidades — mesma altura, sem
   salto de layout quando a contagem cruza uma banda. */
.grace-banner {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.85rem 1.5rem;
}

.grace-banner__icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.grace-banner__body {
  flex: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.grace-banner__title {
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.grace-banner__message {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  font-size: 0.9rem;
}

.grace-banner__item {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  white-space: nowrap;
}

.grace-banner__cta {
  flex-shrink: 0;
  font-weight: 700;
}

/* ── error (1-3 dias, incl. 0): vermelho sólido, texto branco ─────────────── */
.grace-banner--error {
  background: #dc2626;
  color: #ffffff;
}

/* ── warn (4-5 dias): âmbar sólido, texto escuro ──────────────────────────── */
.grace-banner--warn {
  background: #f59e0b;
  color: #451a03;
}

.grace-banner--warn .grace-banner__item {
  background: rgba(255, 255, 255, 0.45);
}

/* ── info (6-10 dias): azul sólido — chamativo, mas sem gritar urgência ───── */
.grace-banner--info {
  background: #2563eb;
  color: #ffffff;
}

/* CTA sempre em alto contraste com a banda: botão claro sobre fundo forte. */
.grace-banner :deep(.p-button) {
  background: #ffffff;
  border-color: #ffffff;
  font-weight: 700;
}

.grace-banner--error :deep(.p-button) {
  color: #b91c1c;
}

.grace-banner--warn :deep(.p-button) {
  color: #92400e;
}

.grace-banner--info :deep(.p-button) {
  color: #1d4ed8;
}
</style>
